import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";

import { ConfirmDeleteModal } from "../components/ConfirmDeleteModal";
import { DynamicForm } from "../components/DynamicForm";
import { Modal } from "../components/Modal";
import { latestSuccessAttempt } from "./after-auth";
import { deleteRecord } from "./delete-record";
import { getApi } from "./get-api";
import { describeWriteError } from "./pg-write-error";
import { DrawerTarget } from "./drawer-url";
import { formUpdateId } from "./row-key";
import { Field, copyRowForCreate, singularize } from "./schema-field";
import { useAuth } from "./useAuth";
import { useDatasources } from "./useDatasources";
import { FieldErrors } from "./validate-record";
import { Button } from "../components/ui/Button";

interface UseDynamicFormProps {
  title?: string;
  datasourceId: string;
  collection: string;
  onSubmit?: (data: any) => Promise<void> | void;
  onClose?: () => void;
  /**
   * Keep the collection URL in step with this drawer. `null` means it closed.
   * Not fired when the collection route itself changed: that navigation already
   * wrote the next `open=` / `new=`, and clearing it here would eat the link.
   */
  onDrawerTarget?: (target: DrawerTarget) => void;
  /** Writers get Delete in the drawer footer and phone More actions menu. */
  canDelete?: boolean;
  schema?: Field[];
  /**
   * Demo datasources render the whole write UI but never call the API: the
   * server rejects writes to them anyway, so simulating locally is the only way
   * a visitor gets to see what the create/edit/delete flow looks like.
   */
  demoMode?: boolean;
  /** Currently loaded grid page. */
  pageRows?: any[];
  /** Total rows matching the grid query, across all pages. */
  rowCount?: number;
  /** Current server-side grid page. */
  paginationModel?: { page: number; pageSize: number };
  /** Requests a grid page through the table's normal pagination path. */
  onPageChange?: (page: number) => void;
  /** True while a requested grid page is loading. */
  pageLoading?: boolean;
  /** Same id the grid uses. Rows with no id are skipped. */
  rowIdOf?: (row: any) => string;
  /** Id written to `open=`; rows without an addressable key write nothing. */
  urlRowIdOf?: (row: any) => string | undefined;
  /** Declared key column; omitted callers still resolve Postgres `id`. */
  idField?: string;
  /**
   * `drawer` (default) wraps the form in the existing Modal so table /
   * EntityPicker / useDataManagement stay unchanged. `inline` renders
   * DynamicForm in place, always as create, and resets after submit.
   */
  variant?: "drawer" | "inline";
  /**
   * Render fields in `schema` order. Generated forms carry a deliberate order;
   * collection drawers do not, and keep the default column ranking.
   */
  preserveFieldOrder?: boolean;
}

type PendingRowNavigation = {
  page: number;
  edge: "first" | "last";
  sourceRows: any[] | undefined;
  loadingObserved: boolean;
};

/** Grid `row.id` first; skip rows the grid could not identify. */
const formRowId = (row: any): string => {
  if (row == null || typeof row !== "object") return "";
  if (row.id != null && row.id !== "") return String(row.id);
  return "";
};

/** Show which row is open, falling back to the table name. */
const recordSubtitle = (row: any, collectionName: string): string => {
  const label = row?.name ?? row?.title ?? row?.label ?? row?.slug;
  if (typeof label === "string" && label.trim()) {
    return `${collectionName} · ${label}`;
  }
  if (row?.id != null) return `${collectionName} · #${row.id}`;
  return collectionName;
};

/**
 * Name the destination table, do not repeat it as the subject. A drawer titled
 * "New Sheet1" above a "Sheet1" subtitle read as if OnlyDash were about to add
 * a tab to the spreadsheet; it adds one row to the tab that is already open.
 */
const createSubtitle = (collectionName: string): string =>
  collectionName?.trim() ? `in ${collectionName.trim()}` : "";

export const useDynamicForm = ({
  title = "Record",
  datasourceId,
  collection: collectionName,
  onSubmit,
  onClose,
  onDrawerTarget,
  canDelete = false,
  schema,
  demoMode,
  pageRows,
  rowCount,
  paginationModel,
  onPageChange,
  pageLoading = false,
  rowIdOf = formRowId,
  urlRowIdOf,
  idField,
  variant = "drawer",
  preserveFieldOrder,
}: UseDynamicFormProps) => {
  const isInline = variant === "inline";
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState<any>(null);
  const [isUpdate, setIsUpdate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverFieldErrors, setServerFieldErrors] = useState<FieldErrors>({});
  const [dirty, setDirty] = useState(false);
  const [footerSlot, setFooterSlot] = useState<HTMLDivElement | null>(null);
  const [createNonce, setCreateNonce] = useState(0);
  const [pendingNav, setPendingNav] = useState<PendingRowNavigation | null>(
    null
  );
  /**
   * Set to the action a dirty form is blocking. Non-null means the drawer body
   * is showing the discard confirm instead of the form.
   */
  const [pendingDiscard, setPendingDiscard] = useState<(() => void) | null>(
    null
  );
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { datasources } = useDatasources();
  const datasource = datasources.find((ds) => ds.id === datasourceId);
  const collection = latestSuccessAttempt(
    datasource?.analyzeAttempts
  )?.databaseCollections?.find((c) => c.name === collectionName)!;

  const isDemo = demoMode ?? !!(datasource as any)?.isDemo;

  const { token } = useAuth();
  const api = useMemo(() => getApi({ token }), [token]);

  const handleOpen = useCallback(
    (initialData?: any, options?: { isUpdate?: boolean; fromUrl?: boolean }) => {
      const data = initialData ?? {};
      const updating =
        options?.isUpdate ??
        (initialData != null && Object.keys(initialData).length > 0);
      setFormData(data);
      setIsUpdate(updating);
      setIsOpen(true);
      setError(null);
      setServerFieldErrors({});
      setDirty(false);
      setPendingDiscard(null);
      // URL-driven opens must not write back: the param is already there, and
      // a create echo would wipe `defaults` off a related-record add link.
      if (!isInline && !options?.fromUrl) {
        if (!updating) onDrawerTarget?.({ kind: "create" });
        else {
          const id = (urlRowIdOf ?? rowIdOf)(data);
          if (id) onDrawerTarget?.({ kind: "edit", id });
        }
      }
    },
    [isInline, onDrawerTarget, rowIdOf, urlRowIdOf]
  );

  const handleClose = useCallback(
    (reason?: "collection") => {
      setIsOpen(false);
      setFormData(null);
      setIsUpdate(false);
      setError(null);
      setServerFieldErrors({});
      setDirty(false);
      setPendingNav(null);
      setPendingDiscard(null);
      if (reason !== "collection" && !isInline) onDrawerTarget?.(null);
      onClose?.();
    },
    [isInline, onClose, onDrawerTarget]
  );

  // The collection route does not remount this hook. A related-record link
  // changes `collection` while the drawer is still open, and the new schema
  // then paints the previous row's overlapping fields under the new title.
  const collectionRef = useRef(collection);
  useEffect(() => {
    if (collectionRef.current === collection) return;
    collectionRef.current = collection;
    handleClose("collection");
  }, [collection, handleClose]);

  const handleSubmit = useCallback(
    async (data: any) => {
      const {
        __changed: changed,
        __addAnother: addAnother,
        ...record
      } = data ?? {};
      const idValue = formUpdateId(formData, record, idField);
      // Inline create stays mounted: same reset as "create and add another".
      const keepOpen = isInline || (!!addAnother && !isUpdate);

      try {
        setLoading(true);
        setError(null);
        setServerFieldErrors({});

        if (isDemo) {
          // Same spinner the real path shows, so the flow reads identically.
          await new Promise((resolve) => setTimeout(resolve, 400));
          toast.success(
            isUpdate
              ? "Saved in the demo only. The demo database is not modified."
              : "Created in the demo only. The demo database is not modified."
          );
          if (keepOpen) {
            await onSubmit?.(record);
            setCreateNonce((n) => n + 1);
            handleOpen({}, { isUpdate: false });
          } else {
            handleClose();
          }
          return;
        }

        if (isUpdate) {
          if (idValue == null) {
            toast.error(
              "This table has no row identity; edits are not possible"
            );
            return;
          }
          const body =
            changed && Object.keys(changed).length ? { ...changed } : { ...record };
          // ARD 9: a Sheets row carries the hash it was read with. The queue
          // compares it with the sheet before it writes, and the audit history
          // reads the old values out of it, so echo it whenever the row has
          // one. The API and the connector both strip it from the cells.
          if (typeof record?._rowHash === "string") {
            body._rowHash = record._rowHash;
          }
          await api.data.updateData(
            datasourceId,
            collectionName,
            idValue,
            body
          );
          toast.success("Record updated");
        } else {
          await api.data.createData(datasourceId, collectionName, changed ?? record);
          toast.success("Record created");
        }

        await onSubmit?.(record);
        if (keepOpen) {
          setCreateNonce((n) => n + 1);
          handleOpen({}, { isUpdate: false });
        } else {
          handleClose();
        }
      } catch (err) {
        const parsed = describeWriteError(err, schema);
        setError(parsed.message);
        setServerFieldErrors(
          parsed.field ? { [parsed.field]: parsed.message } : {}
        );
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      api,
      datasourceId,
      collectionName,
      formData,
      idField,
      isUpdate,
      isDemo,
      isInline,
      schema,
      onSubmit,
      handleClose,
      handleOpen,
    ]
  );

  const resetCreate = useCallback(() => {
    setCreateNonce((n) => n + 1);
    handleOpen({}, { isUpdate: false });
  }, [handleOpen]);

  useEffect(() => {
    if (!isInline) return;
    handleOpen({}, { isUpdate: false });
  }, [handleOpen, isInline]);

  const label = singularize(title);

  const navRows = useMemo(
    () =>
      (pageRows ?? [])
        .map((row, localIndex) => ({ row, localIndex }))
        .filter(({ row }) => rowIdOf(row)),
    [pageRows, rowIdOf]
  );
  const currentNavIndex = isUpdate
    ? navRows.findIndex(
        ({ row }) => rowIdOf(row) === rowIdOf(formData)
      )
    : -1;
  const showRowNav = isUpdate && currentNavIndex >= 0 && navRows.length > 0;
  const pagedNavigation =
    rowCount != null && paginationModel != null && onPageChange != null;
  const currentPage = paginationModel?.page ?? 0;
  const pageSize = paginationModel?.pageSize ?? navRows.length;
  const currentLocalIndex =
    currentNavIndex >= 0 ? navRows[currentNavIndex].localIndex : -1;
  const footerIndex = pagedNavigation
    ? currentPage * pageSize + currentLocalIndex
    : currentNavIndex;
  const footerTotal = pagedNavigation ? rowCount : navRows.length;
  const hasPreviousPage = pagedNavigation && currentPage > 0;
  const hasNextPage =
    pagedNavigation &&
    pageSize > 0 &&
    (currentPage + 1) * pageSize < rowCount;

  /**
   * Runs `action` unless there is unsaved work, in which case it is parked
   * behind an in-drawer confirm step (same shape as BulkEditModal's
   * `confirmingClear`) instead of a `window.confirm` the browser paints
   * outside our chrome.
   */
  const guardDirty = (action: () => void) => {
    if (loading) return;
    if (dirty) {
      setPendingDiscard(() => action);
      return;
    }
    action();
  };

  /** Cancel, X, and Escape share this so a pristine create does not prompt. */
  const requestClose = () => {
    guardDirty(handleClose);
  };

  const goToNavIndex = (index: number) => {
    if (index < 0 || index >= navRows.length) return;
    guardDirty(() => handleOpen(navRows[index].row, { isUpdate: true }));
  };

  const requestAdjacentPage = (
    page: number,
    edge: PendingRowNavigation["edge"]
  ) => {
    if (pendingNav || pageLoading || !onPageChange) return;
    guardDirty(() => {
      setPendingNav({
        page,
        edge,
        sourceRows: pageRows,
        loadingObserved: false,
      });
      onPageChange(page);
    });
  };

  const goToPreviousRow = () => {
    if (currentNavIndex > 0) {
      goToNavIndex(currentNavIndex - 1);
    } else if (hasPreviousPage) {
      requestAdjacentPage(currentPage - 1, "last");
    }
  };

  const goToNextRow = () => {
    if (currentNavIndex < navRows.length - 1) {
      goToNavIndex(currentNavIndex + 1);
    } else if (hasNextPage) {
      requestAdjacentPage(currentPage + 1, "first");
    }
  };

  useEffect(() => {
    if (!pendingNav || !paginationModel) return;
    if (paginationModel.page !== pendingNav.page) return;

    if (pageLoading) {
      if (!pendingNav.loadingObserved) {
        setPendingNav((current) =>
          current && current.page === pendingNav.page
            ? { ...current, loadingObserved: true }
            : current
        );
      }
      return;
    }

    if (!pendingNav.loadingObserved && pageRows === pendingNav.sourceRows) return;

    const target =
      pendingNav.edge === "first"
        ? navRows[0]
        : navRows[navRows.length - 1];
    setPendingNav(null);
    if (target) handleOpen(target.row, { isUpdate: true });
  }, [
    handleOpen,
    navRows,
    pageLoading,
    pageRows,
    paginationModel,
    pendingNav,
  ]);

  const confirmDelete = async () => {
    if (deleting) return;
    const id = formUpdateId(formData, formData, idField);
    if (id == null) {
      setConfirmingDelete(false);
      setError("This table has no row identity; deletes are not possible");
      return;
    }
    setDeleting(true);
    setError(null);
    setServerFieldErrors({});
    try {
      await deleteRecord(api, datasourceId, collectionName, String(id), {
        rowHash: formData?._rowHash,
      });
      setConfirmingDelete(false);
      toast.success("Deleted");
      handleClose();
      await onSubmit?.(formData);
    } catch (err) {
      setConfirmingDelete(false);
      setError(describeWriteError(err, schema).message);
    } finally {
      setDeleting(false);
    }
  };

  const showDelete = canDelete && !isDemo && !isInline && isUpdate;

  const handleDuplicate = () => {
    guardDirty(() => {
      setCreateNonce((n) => n + 1);
      handleOpen(copyRowForCreate(formData, schema ?? []), { isUpdate: false });
    });
  };

  const formInstanceKey = isUpdate
    ? rowIdOf(formData) || String(formData?.id ?? "edit")
    : `create-${createNonce}`;

  const form = (
    <DynamicForm
      key={formInstanceKey}
      collection={collection}
      schema={schema}
      model={formData}
      onSubmit={handleSubmit}
      onCancel={isInline ? resetCreate : requestClose}
      onDelete={
        showDelete ? () => guardDirty(() => setConfirmingDelete(true)) : undefined
      }
      onDuplicate={
        isInline || !isUpdate ? undefined : handleDuplicate
      }
      onDirtyChange={setDirty}
      submitting={loading}
      submitError={error}
      serverFieldErrors={serverFieldErrors}
      // Simulation belongs to this hook. DynamicForm still renders its normal
      // submit chrome so visitors can complete the demo flow.
      demoMode={false}
      preserveFieldOrder={preserveFieldOrder}
      isUpdate={isInline ? false : isUpdate}
      footerSlot={isInline || pendingDiscard ? undefined : footerSlot}
      createLabel="Create record"
      debugMode={false}
      rowNav={
        !isInline && showRowNav
          ? {
              index: footerIndex,
              total: footerTotal,
              canPrev: currentNavIndex > 0 || hasPreviousPage,
              canNext: currentNavIndex < navRows.length - 1 || hasNextPage,
              busy: pageLoading || pendingNav != null,
              onPrev: goToPreviousRow,
              onNext: goToNextRow,
            }
          : undefined
      }
    />
  );

  const discardConfirm = pendingDiscard && (
    <div className="flex flex-col gap-4 text-sm text-od-text">
      <p role="alert" className="m-0">
        Discard your unsaved changes?
      </p>
      <p className="m-0 text-xs text-od-muted">
        Anything you typed in this form is lost.
      </p>
      <div className="flex justify-end gap-2">
        <Button
          onClick={() => setPendingDiscard(null)}
          variant="secondary" size="sm"
        >
          Keep editing
        </Button>
        <Button
          onClick={() => {
            const action = pendingDiscard;
            setPendingDiscard(null);
            setDirty(false);
            action();
          }}
          variant="danger-solid"
          size="sm"
        >
          Discard changes
        </Button>
      </div>
    </div>
  );

  const FormModal: ReactNode = isInline ? (
    form
  ) : (
    <Modal
      open={isOpen}
      title={isUpdate ? `Edit ${label}` : "New record"}
      subtitle={
        isUpdate
          ? recordSubtitle(formData, collectionName)
          : createSubtitle(collectionName)
      }
      onClose={requestClose}
      footerRef={setFooterSlot}
      variant="drawer"
    >
      {discardConfirm}
      {/* Kept mounted while confirming so "Keep editing" restores the edits. */}
      <div className={pendingDiscard ? "hidden" : undefined}>{form}</div>
      <ConfirmDeleteModal
        open={confirmingDelete}
        onCancel={() => setConfirmingDelete(false)}
        onConfirm={confirmDelete}
        applying={deleting}
        progress="Deleting…"
      />
    </Modal>
  );

  return {
    FormModal,
    openForm: handleOpen,
    closeForm: handleClose,
    formData,
    isOpen,
    loading,
    error,
  };
};
